import { onBeforeUnmount, onMounted, ref } from 'vue';
import { APP_OPTION_PER_PAGE } from 'assets/js/consts/AppOptions.const.js';
import { useCookie, useFetch } from 'nuxt/app';
import inputs_to_key_value from 'assets/js/translators/inputs_to_key_value.translator.js';
import make_bearer_token from 'assets/js/functions/make_bearer_token.js';
import get_base_url from '~/assets/js/functions/route/get_base_url';
import make_query_params from '~/assets/js/functions/route/make_query_params';
import useAuth from '~/assets/js/composables/other/useAuth';

const useServerListFetch = async (request_url, options) => {
  options = Object.assign(
    {
      placeholder: {
        list: [],
        total: 0,
      },
      filters: {},
      in_load: false,
      lazy: false,
      server: true,
      watch: false,
      onResponse() {},
      listSelector: '.js-infiniteList',
    },
    options
  );

  const token = useAuth().getAuthToken();

  const is_loading = ref(options.in_load);
  const filters = ref({
    skip: {
      value: 0,
    },
    take: {
      value: APP_OPTION_PER_PAGE,
    },
    ...options.filters,
  });

  const fetch = async () => {
    const queries = make_query_params(inputs_to_key_value(filters.value));

    const { data, pending, refresh, execute, error, status } = await useFetch(
      request_url + queries,
      {
        ...options,
        headers: {
          'Accept': 'application/json',
          'Authorization': token ? make_bearer_token(token) : '',
          'X-Currency': 'IRT', // IRT | IRR
        },
        default: () => options.placeholder,
        baseURL: get_base_url(),
        transform(data) {
          return data.data;
        },
        onRequest() {
          addScrollEvent();

          is_loading.value = true;
        },
        onRequestError() {
          is_loading.value = false;
        },
        onResponse(rs) {
          options.onResponse(rs.response._data.data);
          is_loading.value = false;
        },
        onResponseError({ response }) {
          is_loading.value = false;
        },
      }
    );

    return {
      data,
      pending,
      refresh,
      execute,
      error,
      status,
    };
  };

  const fresh_fetch = async () => {
    filters.value.skip.value = 0;

    if (import.meta.client) {
      addScrollEvent();
    }

    const { data: inner_data } = await fetch();

    data.value = inner_data.value;
  };

  const filter = async () => {
    await fresh_fetch();
  };

  const handle_scroll = () => {
    const scrollOffset = 50;
    const listElement = document.querySelector(options.listSelector);

    if (listElement) {
      // Calculate the distance from the top of the viewport to the bottom of the list element
      const listBottom = listElement.getBoundingClientRect().bottom;
      // Check if the bottom of the list element is within the viewport plus the offset
      if (listBottom <= window.innerHeight + scrollOffset) {
        if (is_loading.value) return;

        if (filters.value.skip.value + APP_OPTION_PER_PAGE >= data.value.total) {
          removeScrollEvent();
          return;
        }

        load_more();
      }
    } else {
      // Original logic for scrolling the entire document body
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight - scrollOffset) {
        if (is_loading.value) return;

        if (filters.value.skip.value + APP_OPTION_PER_PAGE >= data.value.total) {
          removeScrollEvent();
          return;
        }

        load_more();
      }
    }
  };

  const load_more = async () => {
    filters.value.skip.value += APP_OPTION_PER_PAGE;

    const { data: inner_data } = await fetch();

    data.value.list = [...data.value.list, ...inner_data.value.list];

    if (data.value.list.length === data.value.total) {
      removeScrollEvent();
    }
  };

  onMounted(() => {
    if (import.meta.server) return;

    if (!data.value.list.length || data.value.list.length === data.value.total) return;

    addScrollEvent();
  });

  onBeforeUnmount(() => {
    removeScrollEvent();
  });

  const addScrollEvent = () => {
    if (!import.meta.client) return;

    removeScrollEvent();
    window.addEventListener('scroll', handle_scroll);
  };

  const removeScrollEvent = () => {
    if (!import.meta.client) return;

    window.removeEventListener('scroll', handle_scroll);
  };

  const { data, pending, refresh, execute, error, status } = await fetch();

  if (import.meta.client) {
    is_loading.value = status.value === 'pending';
  }

  if (error.value) {
    console.error(error.value);
  }

  const has_error = computed(() => Boolean(error.value));

  return {
    data,
    filters,
    is_loading,
    refresh,
    has_error,
    status,
    fetch: fresh_fetch,
    filter,
  };
};

export default useServerListFetch;
